<template>
  <div>
    <!------------ START: Variables field ------------>
    <VariablesField
      v-if="isVariablesField"
      v-model="$v.value.$model"
      :field="field"
      :field-wrapper="true"
      :show-toggle="true"
      @toggle-variables-field="toggleVariablesField"
    />
    <!------------ END: Variables field ------------>
    <!------------ START: FieldWrapper ------------>
    <FieldWrapper
      v-else
      ref="fieldWrapper"
      :field="field"
      :input-group="true"
      :variables-field-active="variablesFieldActive"
      @toggle-variables-field="toggleVariablesField"
    >
      <!------------ START: Multiselect ------------>
      <component
        :is="componentType"
        v-if="!isVariablesField && componentType"
        ref="select"
        v-model="internalValue"
        :items="items"
        item-text="label"
        item-value="value"
        :return-object="shouldReturnObject"
        chips
        deletable-chips
        class="form-control h-auto"
        :class="[validationClass, isDisabled ? 'disabled' : '']"
        :clearable="field.clearable"
        clear-icon="fal fa-xmark icon-md"
        append-icon="fal fa-chevron-down icon-md"
        :menu-props="{ offsetY: true }"
        :no-data-text="$t('formHelper.noSelectOptions')"
        :placeholder="getSnippet(field.placeholder)"
        :disabled="isDisabled"
        multiple
        :loading="isLoading || isLoadingDataOptions"
        @focus="onOpen"
        @keydown="search"
      >
        <template v-if="field?.optionsConfig?.async" #no-data>
          <div class="async-options-wrapper">
            <div v-if="isLoadingDataOptions" class="select-loading-wrapper">
              <Loader />

              <div class="alert alert-info">
                {{ $t("formHelper.dataOptions.loading") }}
              </div>
            </div>

            <div v-else class="alert alert-info">
              {{ $t("formHelper.dataOptions.search") }}
            </div>
          </div>
        </template>

        <template #selection="{ item }">
          <v-chip
            v-b-popover.hover="item.isInvalid ? invalidOptionTooltip : ''"
            :class="{
              'is-invalid': item.isInvalid
            }"
            close
            @click:close="removeSelectedOption(item)"
          >
            {{ item.label ?? item }}</v-chip
          >
        </template>
      </component>

      <OptionsReloadButton
        v-if="field?.optionsConfig?.async"
        @reload="reloadDataOptions"
      ></OptionsReloadButton>

      <!------------ END: Multiselect ------------>
    </FieldWrapper>
    <!------------ END: FieldWrapper ------------>
    <!------------ START: Error messages tooltip ------------>
    <ErrorsTooltip
      v-if="isMounted"
      :target="$refs.fieldWrapper?.$refs.inputGroup"
      :errors="validationErrors"
    />
    <!------------ END: Error messages tooltip ------------>
  </div>
</template>

<script>
import {
  base,
  combobox,
  eagerValidation,
  select,
  variablesField
} from "@/components/Tools/FormHelper/Helper/mixins";
import FieldWrapper from "@/components/Tools/FormHelper/Components/FieldWrapper";
import ErrorsTooltip from "@/components/Tools/FormHelper/Components/ErrorsTooltip";
import VariablesField from "@/components/Tools/FormHelper/Components/InputVariables";
import Loader from "@/components/Tools/Loader.vue";
import OptionsReloadButton from "@/components/Tools/FormHelper/Components/OptionsReloadButton.vue";
import { VCombobox, VAutocomplete } from "vuetify/lib/components";

export default {
  components: {
    OptionsReloadButton,
    Loader,
    FieldWrapper,
    ErrorsTooltip,
    VariablesField,
    VAutocomplete,
    VCombobox
  },
  mixins: [base, select, combobox, variablesField, eagerValidation],
  props: {},
  data() {
    return {
      isMounted: false
    };
  },
  computed: {
    invalidOptionTooltip() {
      const translationExists = this.$te(
        `${this.options.snippetPrefix}.invalidOption`
      );

      return translationExists
        ? this.$t(`${this.options.snippetPrefix}.invalidOption`)
        : this.$t("general.invalidOption");
    },
    shouldReturnObject() {
      // Use return-object="true" for combobox (editable) to show labels correctly
      // Use return-object="false" for autocomplete (non-editable) for current behavior
      return this.field.isEditable;
    },
    internalValue: {
      get() {
        if (this.shouldReturnObject) {
          // When using return-object="true", find the full objects from items
          const valueArray = Array.isArray(this.$v.value.$model)
            ? this.$v.value.$model
            : [];
          return valueArray.map(
            val =>
              this.items.find(item => item.value === val) || {
                value: val,
                label: val
              }
          );
        }
        // When using return-object="false", use the primitive values directly
        return this.$v.value.$model;
      },
      set(newValue) {
        if (this.shouldReturnObject && Array.isArray(newValue)) {
          // Extract primitive values from objects and emit them
          const primitiveValues = newValue.map(item =>
            typeof item === "object" && item.value !== undefined
              ? item.value
              : item
          );
          this.$v.value.$model = primitiveValues;
        } else {
          // Direct primitive values
          this.$v.value.$model = newValue;
        }
      }
    }
  },
  mounted() {
    this.isMounted = true;
  },
  methods: {
    removeSelectedOption(item) {
      const valueClone = Array.isArray(this.$v.value.$model)
        ? [...this.$v.value.$model]
        : [];

      // Handle both object items (when return-object="true") and primitive values
      const valueToRemove =
        typeof item === "object" && item.value !== undefined
          ? item.value
          : item;

      const index = valueClone.indexOf(valueToRemove);

      if (index > -1) {
        valueClone.splice(index, 1);
        this.$v.value.$model = valueClone;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$chip-error-background: lighten($color-connect-error, 35%);

:deep(.v-select) {
  &.v-select--is-multi {
    .v-select__selections {
      align-items: flex-start;

      span {
        padding: 0 0.35rem !important;
        border-radius: 6px;
        background-color: var(--ax-bg-secondary, #{$color-connect-background});

        &.is-invalid {
          border: 1px solid $color-connect-error;
          background: $chip-error-background;

          > .v-chip__content {
            background: $chip-error-background;
          }
        }
      }
    }

    input {
      max-height: 32px !important; // Prevent the placeholder from being cut
    }
  }

  .v-chip {
    &.v-size--default {
      height: 25px;
      margin-bottom: 2px;
    }
  }

  .v-ripple__container {
    display: none !important;
  }

  &.v-select--chips {
    .v-select__selections {
      min-height: unset;
    }
  }

  .v-input__slot {
    margin: 0;
    padding: 5px 1rem;
  }

  .v-text-field__details {
    display: none;
  }
}

:deep(.form-control) {
  &.is-invalid,
  &.is-valid {
    background-position: right 0.75rem top 0.6rem;
  }
}

.select-loading-wrapper {
  min-height: 150px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.async-options-wrapper {
  padding: 10px 10px 0;
}
</style>
